import type { LogsEvent } from "@datadog/browser-logs";
import type { RumEvent } from "@datadog/browser-rum-slim";

// These ids, and numeric tenant ids, resolve to a parker's permit, payment, order or unit through public lookups.
const KEYED_ID_SEGMENT = /\/(confirmation|permits|transactions|payment)\/[^/]+/g;
const REGISTRATION_NUMBER = /\/portal\/registration\/(?!payment(?:\/|$))[^/]+/g;
const NUMERIC_SEGMENT = /\/\d+(?=\/|$)/g;
const EMAIL = /[^\s@"'<>()/]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)*\.[A-Za-z]{2,}/g;
// Stripe object ids and client secrets tie an error to a parker's payment.
const STRIPE_ID = /\b(?:pi|pm|seti|cus|ch|py|src|tok|card|in)_[A-Za-z0-9]{14,}(?:_secret_[A-Za-z0-9]+)?/g;

// Query strings and fragments carry tokens, emails and plate searches; paths are enough for aggregate stats.
export function redactUrl(url: string): string {
	const end = url.search(/[?#]/);
	const path = end === -1 ? url : url.slice(0, end);
	return path
		.replace(KEYED_ID_SEGMENT, "/$1/:id")
		.replace(REGISTRATION_NUMBER, "/portal/registration/:id")
		.replace(NUMERIC_SEGMENT, "/:id");
}

export function redactText(text: string): string {
	return text.replace(EMAIL, "<email>").replace(STRIPE_ID, "<stripe-id>");
}

function redactIfUrl(value: string): string {
	return /^https?:\/\//.test(value) ? redactUrl(value) : value;
}

const POSTHOG_URL_PROPERTIES = [
	"$current_url",
	"$pathname",
	"$referrer",
	"$prev_pageview_pathname",
	"$initial_current_url",
	"$initial_pathname",
	"$initial_referrer",
];

type PostHogProperties = Record<string, unknown>;
type PostHogCapture = { properties: PostHogProperties; $set?: PostHogProperties; $set_once?: PostHogProperties };

function redactPostHogProperties(properties: PostHogProperties | undefined): void {
	for (const key of POSTHOG_URL_PROPERTIES) {
		const value = properties?.[key];
		if (properties && typeof value === "string") {
			properties[key] = redactUrl(value);
		}
	}
}

export function redactPostHogEvent<T extends PostHogCapture | null>(capture: T): T {
	if (capture) {
		redactPostHogProperties(capture.properties);
		redactPostHogProperties(capture.$set);
		redactPostHogProperties(capture.$set_once);
	}
	return capture;
}

// No cookies or storage (PostHog derives a server-side hash of IP and browser instead), no session replay, no ad
// click ids or timezone, redacted URLs.
export const redactedPostHogConfig = {
	cookieless_mode: "always" as const,
	disable_session_recording: true,
	mask_personal_data_properties: true,
	property_denylist: ["$timezone", "$timezone_offset"],
	before_send: redactPostHogEvent,
};

export function redactRumEvent(event: RumEvent): void {
	event.view.url = redactUrl(event.view.url);
	if (event.view.referrer) {
		event.view.referrer = redactUrl(event.view.referrer);
	}

	if (event.type === "view") {
		const lcp = event.view.performance?.lcp;
		if (lcp?.resource_url) {
			lcp.resource_url = redactUrl(lcp.resource_url);
		}
	} else if (event.type === "resource") {
		event.resource.url = redactUrl(event.resource.url);
	} else if (event.type === "error") {
		event.error.message = redactText(event.error.message);
		if (event.error.stack) {
			event.error.stack = redactText(event.error.stack);
		}
		if (event.error.resource) {
			event.error.resource.url = redactUrl(event.error.resource.url);
		}
	} else if (event.type === "long_task") {
		for (const script of event.long_task.scripts ?? []) {
			if (script.source_url) {
				script.source_url = redactUrl(script.source_url);
			}
			if (script.invoker) {
				script.invoker = redactIfUrl(script.invoker);
			}
		}
	}
}

export function redactLogsEvent(event: LogsEvent): void {
	event.view.url = redactUrl(event.view.url);
	if (event.view.referrer) {
		event.view.referrer = redactUrl(event.view.referrer);
	}

	if (event.http) {
		const redactedUrl = redactUrl(event.http.url);
		event.message = event.message.replace(event.http.url, redactedUrl);
		event.http.url = redactedUrl;
	}

	event.message = redactText(event.message);
	if (event.error?.message) {
		event.error.message = redactText(event.error.message);
	}
	if (event.error?.stack) {
		event.error.stack = redactText(event.error.stack);
	}
}
